import React, { useEffect, useState } from 'react';
import { AuditLogEventName, StyledDivider } from 'pages/AuditLogPage/AuditLogViewPage/styles';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTranslations } from 'hooks/useTranslations';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { useNavigate } from 'react-router-dom';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { ERouteLinks } from 'models/route';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { formatDate } from 'utils/date';
import { IAuditLogItemResponse } from 'models/auditLog';
import { Chips } from 'components/atoms/Chips';
import { findInDictionary } from 'helpers/dictionary';
import { licencePrintStatusVariant } from 'pages/LicencesPage/helpers';
import { useDictionary } from 'models/dictionaryContext';

export interface IPrintoutBatchObject {
  uniqueId?: string;
  name?: string;
  licencingAuthorityUniqueId?: string;
  statusUniqueId?: string;
  createdDate?: string;
  manifestDate?: string;
}

interface ILegalEntityLicencesPrintoutBatchEventDetails {
  auditLog: IAuditLogItemResponse;
}

interface IRenderDetails {
  batchDetail: IPrintoutBatchObject;
  t: any;
}

const RenderDetails = ({ batchDetail, t }: IRenderDetails) => {
  const { longDateFormat, getProperties } = useGlobalProperty();

  useEffect(() => {
    getProperties();
  }, []);

  const {
    weaponLicencePrintBatchStatus,
    getWeaponLicencePrintBatchStatus,
    weaponLicencingAuthority,
    getWeaponLicencingAuthority,
  } = useDictionary();

  useEffect(() => {
    getWeaponLicencePrintBatchStatus();
    getWeaponLicencingAuthority();
  }, []);

  return (
    <>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.batchName.label')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(batchDetail?.name)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.licenceAuthority.label')}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(
            findInDictionary(
              batchDetail?.licencingAuthorityUniqueId,
              weaponLicencingAuthority,
            ),
          )}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.batchStatus.label')}
        </Typography>
        <Typography variant="body2">
          {batchDetail.statusUniqueId ? (
            <Chips
              text={findInDictionary(
                batchDetail.statusUniqueId,
                weaponLicencePrintBatchStatus,
              )}
              fullWidth
              variant={licencePrintStatusVariant(batchDetail.statusUniqueId)}
            />
          ) : (
            '-'
          )}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.createDate.label')}
        </Typography>
        <Typography variant="body2">
          {batchDetail?.createdDate
            ? formatDate(batchDetail.createdDate, longDateFormat)
            : emptyFieldHolder('')}
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.manifestDate.label')}
        </Typography>
        <Typography variant="body2">
          {batchDetail?.manifestDate
            ? formatDate(batchDetail.manifestDate, longDateFormat)
            : emptyFieldHolder('')}
        </Typography>
      </Grid>
    </>
  );
};

const LegalEntityLicencesPrintoutBatchEventDetails = ({
  auditLog,
}: ILegalEntityLicencesPrintoutBatchEventDetails) => {
  const [batchDetails, setPrintoutBatchDetails] = useState<IPrintoutBatchObject | null>(null);
  const [previousPrintoutBatchDetails, setPreviousPrintoutBatchDetails] =
    useState<IPrintoutBatchObject | null>(null);
  const { t } = useTranslations();
  const navigate = useNavigate();

  useEffect(() => {
    if (auditLog?.postObjectValue) {
      setPrintoutBatchDetails(auditLog.postObjectValue as unknown as IPrintoutBatchObject);
    } else {
      setPrintoutBatchDetails(null);
    }

    if (auditLog?.previousObjectValue) {
      setPreviousPrintoutBatchDetails(
        auditLog.previousObjectValue as unknown as IPrintoutBatchObject,
      );
    } else {
      setPreviousPrintoutBatchDetails(null);
    }
  }, [auditLog]);

  const handleRedirect = (details?: IPrintoutBatchObject | null) => {
    if (!details?.uniqueId) return;

    navigate(`${ERouteLinks.PrintoutBatchDetails.replace(':id', details.uniqueId)}`);
  };

  return (
    <>
      {previousPrintoutBatchDetails && (
        <>
          <AuditLogEventName>
            <Typography variant="h5">
              {`${t('module.batches.name')} ${t('auditLogDetails.before.event.title')}`}
            </Typography>
          </AuditLogEventName>
          <DetailsTemplate
            title={previousPrintoutBatchDetails?.name || ''}
            pageType={EDetailsPageTypes.auditLogView}
            redirectToView={() => handleRedirect(previousPrintoutBatchDetails)}
          >
            <RenderDetails batchDetail={previousPrintoutBatchDetails} t={t} />
          </DetailsTemplate>
        </>
      )}

      {batchDetails && (
        <>
          <AuditLogEventName sx={{ marginTop: previousPrintoutBatchDetails ? '20px' : '0' }}>
            <Typography variant="h5">
              {`${t('module.batches.name')} ${t(
                previousPrintoutBatchDetails
                  ? 'auditLogDetails.after.event.title'
                  : 'auditLogDetails.post.event.title',
              )}`}
            </Typography>
          </AuditLogEventName>
          <DetailsTemplate
            title={batchDetails?.name || ''}
            pageType={EDetailsPageTypes.auditLogView}
            redirectToView={() => handleRedirect(batchDetails)}
          >
            <RenderDetails batchDetail={batchDetails} t={t} />
          </DetailsTemplate>
        </>
      )}
    </>
  );
};

export default LegalEntityLicencesPrintoutBatchEventDetails;
